import { addDays } from './calendar.js';
import type { IsoDate } from './calendar.js';
import { addWeeks, mondayOf } from './iso-week.js';
import type { IsoWeek } from './iso-week.js';

/**
 * Die Kapazitaet eines Mitarbeiters je ISO-Kalenderwoche (AC-103) — die EINE
 * Stelle, an der sie berechnet wird.
 *
 * Wer kuenftig Kapazitaet braucht — das Wochenraster (AC-402), die Auslastung
 * mit Fachgruppensummen (AC-407), die serverseitige 26-Wochen-Aggregation
 * (AC-410), die Feiertage (AC-110) —, ruft diese Funktionen auf und rechnet
 * nicht daneben. Zwei Rechnungen liefen auseinander, und zwar genau an den
 * Raendern, die AC-103 regelt: Eintrittswoche, Austrittswoche, Wechsel der
 * Soll-Wochenstunden.
 *
 * DAS MODELL, in einem Satz: Eine Woche hat fuenf Arbeitstage (Mo–Fr), jeder
 * davon traegt ein Fuenftel der Soll-Wochenstunden, die AN DIESEM TAG gelten —
 * aber nur, wenn die Person an diesem Tag beschaeftigt ist.
 *
 * Daraus folgen die drei Akzeptanzkriterien ohne Sonderfall:
 *
 *   AK-1  Nach dem Austritt ist kein Tag beschaeftigt: 0 Kapazitaet, nicht
 *         planbar. Die Austrittswoche selbst zaehlt anteilig bis zum letzten
 *         Arbeitstag.
 *   AK-2  Vor dem Eintritt ebenso; ab dem Eintrittstag planbar, in der
 *         Eintrittswoche anteilig.
 *   AK-3  Jeder Tag liest den Eintrag, der an IHM gilt. Ein neuer Eintrag ab
 *         KW 40 aendert KW 38 und 39 nicht — sie lesen weiter den alten.
 *
 * Gerechnet wird in Hundertstelstunden als Ganzzahl. Ein Fuenftel davon ist
 * nicht immer ganzzahlig (38.33 h / 5); die Wochensumme wird deshalb exakt
 * gebildet und erst am Ende auf Hundertstel gerundet. Ein Unentschieden kann
 * dabei nicht entstehen: ein Fuenftel einer Ganzzahl endet auf .0, .2, .4, .6
 * oder .8.
 *
 * Seit der AC-103-Ergaenzung (JF 07.10.2026) hat nur eine PLANBARE Person
 * Kapazitaet: intern UND vollstaendig gepflegt (`isPlannable`). Externe
 * Dienstleister und Stammsaetze aus PACS, die noch zu vervollstaendigen sind,
 * stehen in jeder Woche als `nicht_planbar` mit 0 Stunden da — hier, nicht bei
 * jedem Aufrufer.
 *
 * Abwesenheiten (AC-406) stehen je WOCHE, nicht je Tag, und werden deshalb
 * nach der Tagesschleife abgezogen: `verfuegbareKapazitaet` unten. Was hier
 * BEWUSST fehlt: Feiertage (AC-110) und Kapazitaetskorrekturen (AC-106). Sie
 * setzen an der Tagesschleife an — ein Feiertag ist ein Arbeitstag ohne
 * Anteil — und gehoeren in diese Datei.
 *
 * Transportfrei (ADR-04): die Oberflaeche rechnet ueber `@asas/domain` mit
 * denselben Funktionen; die Schemata dazu stehen in `employees.ts`. Wochen und
 * Montage kommen aus `iso-week.ts` (AC-109), nur die Tagesarithmetik aus
 * `calendar.ts`.
 */

/** Das Ladefenster des Wochenrasters (ADR-07): hoechstens 26 Wochen je Abfrage. */
export const CAPACITY_WEEKS_MAX = 26;

/** Mo–Fr. Samstag und Sonntag tragen keine Soll-Stunden. */
export const WORKDAYS_PER_WEEK = 5;

/**
 * Wandelt `"38.5"` in `3850` um. Vorausgesetzt ist eine nicht negative
 * Dezimalzeichenkette mit Punkt und hoechstens zwei Nachkommastellen
 * (`weeklyHoursSchema`, `storedWeeklyHoursSchema`). Die Zerlegung laeuft ueber
 * die Zeichenkette und nie ueber `Number("38.5") * 100` (= 3849.9999…).
 */
export function weeklyHoursToHundredths(value: string): number {
  const [ganz = '', teil = ''] = value.split('.');

  return Number(ganz) * 100 + Number(teil.padEnd(2, '0'));
}

/** Die Umkehrung: `3850` -> `"38.50"`. */
export function hundredthsToHours(value: number): string {
  return `${String(Math.floor(value / 100))}.${String(value % 100).padStart(2, '0')}`;
}

export interface EmploymentPeriod {
  readonly startsOn: IsoDate | null;
  readonly endsOn: IsoDate | null;
}

export interface WeeklyHoursEntry {
  readonly effectiveFrom: IsoDate;
  readonly hours: string;
}

/**
 * Intern oder extern (AC-103, Ergaenzung). Externe Dienstleister buchen Zeit in
 * PACS, gehoeren aber zu keiner Gesellschaft und keiner Fachgruppe und haben
 * keine Kapazitaet.
 */
export const EMPLOYEE_TYPES = ['intern', 'extern'] as const;
export type EmployeeType = (typeof EMPLOYEE_TYPES)[number];

/**
 * Ob ein Stammsatz vollstaendig gepflegt ist. `zu_vervollstaendigen`: aus einem
 * PACS-Kontakt entstanden, Typ und Pflichtangaben fehlen noch.
 */
export const EMPLOYEE_RECORD_STATUSES = ['vollstaendig', 'zu_vervollstaendigen'] as const;
export type EmployeeRecordStatus = (typeof EMPLOYEE_RECORD_STATUSES)[number];

/** Was ueber die Planbarkeit einer Person entscheidet. `personType: null`: noch unbekannt. */
export interface PlanningProfile {
  readonly personType: EmployeeType | null;
  readonly recordStatus: EmployeeRecordStatus;
}

/**
 * Planbar ist, wer intern UND vollstaendig ist. Nur fuer sie sind Gesellschaft,
 * Fachgruppe und Soll Pflicht, und nur sie haben Kapazitaet — dieselbe
 * Bedingung pruefen die Trigger der Datenbank.
 */
export function isPlannable(profile: PlanningProfile): boolean {
  return profile.personType === 'intern' && profile.recordStatus === 'vollstaendig';
}

export interface CapacityEmployee extends EmploymentPeriod, PlanningProfile {
  readonly weeklyHours: readonly WeeklyHoursEntry[];
}

/**
 * Wie die Woche zur Beschaeftigung liegt.
 *
 *   vor_eintritt   kein Arbeitstag beschaeftigt, die Beschaeftigung kommt noch
 *   beschaeftigt   alle fuenf Arbeitstage beschaeftigt
 *   teilweise      ein bis vier Arbeitstage — Eintritts- oder Austrittswoche
 *   nach_austritt  kein Arbeitstag beschaeftigt, die Beschaeftigung ist vorbei
 *   nicht_planbar  die Person ist nicht planbar (`isPlannable`): extern oder
 *                  noch zu vervollstaendigen — unabhaengig von der Woche
 */
export const WEEK_CAPACITY_STATUSES = [
  'vor_eintritt',
  'beschaeftigt',
  'teilweise',
  'nach_austritt',
  'nicht_planbar',
] as const;
export type WeekCapacityStatus = (typeof WEEK_CAPACITY_STATUSES)[number];

/** Die Kapazitaet einer Kalenderwoche. Das Schema dazu ist `weekCapacitySchema` (employees.ts). */
export interface WeekCapacity {
  readonly isoYear: number;
  readonly isoWeek: number;
  /** Der Montag der Woche. */
  readonly weekStart: IsoDate;
  readonly status: WeekCapacityStatus;
  /** Mindestens ein Arbeitstag der Woche liegt in der Beschaeftigung (AK-2). */
  readonly planbar: boolean;
  /** Arbeitstage (Mo–Fr) innerhalb der Beschaeftigung, 0 bis 5. */
  readonly employedWorkdays: number;
  /** Die Kapazitaet der Woche in Stunden, als Dezimalzeichenkette (`"36.80"`). */
  readonly hours: string;
  /**
   * An mindestens einem beschaeftigten Arbeitstag gilt KEIN Eintrag der
   * Soll-Historie — bei Altbestand ohne Soll-Angabe. Die Kapazitaet dieser
   * Tage ist 0, und das soll auffallen statt still als Wert dazustehen.
   */
  readonly missingWeeklyHours: boolean;
}

/** Beschaeftigt an diesem Tag? Eintritt und Austritt zaehlen beide mit. */
export function isEmployedOn(period: EmploymentPeriod, date: IsoDate): boolean {
  return (
    (period.startsOn === null || period.startsOn <= date) &&
    (period.endsOn === null || date <= period.endsOn)
  );
}

/**
 * Die Soll-Wochenstunden, die an diesem Tag gelten: der Eintrag mit dem
 * spaetesten Wirksamkeitsdatum, das nicht nach dem Tag liegt. `null`, wenn der
 * Tag vor dem ersten Eintrag liegt. Die Reihenfolge der Eintraege spielt keine
 * Rolle.
 */
export function weeklyHoursOn(entries: readonly WeeklyHoursEntry[], date: IsoDate): string | null {
  let gilt: WeeklyHoursEntry | null = null;

  for (const entry of entries) {
    if (
      entry.effectiveFrom <= date &&
      (gilt === null || entry.effectiveFrom > gilt.effectiveFrom)
    ) {
      gilt = entry;
    }
  }

  return gilt?.hours ?? null;
}

/** Die Kapazitaet einer Kalenderwoche. */
export function weekCapacity(employee: CapacityEmployee, week: IsoWeek): WeekCapacity {
  const weekStart = mondayOf(week);

  if (!isPlannable(employee)) {
    const { isoYear, isoWeek } = week;

    return {
      isoYear,
      isoWeek,
      weekStart,
      status: 'nicht_planbar',
      planbar: false,
      employedWorkdays: 0,
      hours: '0.00',
      missingWeeklyHours: false,
    };
  }

  let employedWorkdays = 0;
  let missingWeeklyHours = false;
  // Summe der Tagesanteile mal fuenf, in Hundertstelstunden — exakt.
  let fuenffach = 0;

  for (let offset = 0; offset < WORKDAYS_PER_WEEK; offset += 1) {
    const tag = addDays(weekStart, offset);

    if (!isEmployedOn(employee, tag)) continue;

    employedWorkdays += 1;
    const hours = weeklyHoursOn(employee.weeklyHours, tag);

    if (hours === null) {
      missingWeeklyHours = true;
    } else {
      fuenffach += weeklyHoursToHundredths(hours);
    }
  }

  const freitag = addDays(weekStart, WORKDAYS_PER_WEEK - 1);
  const status: WeekCapacityStatus =
    employedWorkdays === WORKDAYS_PER_WEEK
      ? 'beschaeftigt'
      : employedWorkdays > 0
        ? 'teilweise'
        : employee.startsOn !== null && employee.startsOn > freitag
          ? 'vor_eintritt'
          : 'nach_austritt';

  // `mondayOf` (iso-week.ts) weist eine Woche ab, die es nicht gibt (KW 53
  // eines Jahres mit 52); die Bezeichnung ist damit die der Eingabe.
  const { isoYear, isoWeek } = week;

  return {
    isoYear,
    isoWeek,
    weekStart,
    status,
    planbar: employedWorkdays > 0,
    employedWorkdays,
    // Kaufmaennisch auf Hundertstel: floor(x / 5 + 1/2) = floor((2x + 5) / 10).
    hours: hundredthsToHours(Math.floor((2 * fuenffach + 5) / 10)),
    missingWeeklyHours,
  };
}

/** Die Kapazitaet fuer `weeks` aufeinanderfolgende Kalenderwochen ab `from`, ueber Jahresgrenzen hinweg. */
export function capacityForWeeks(
  employee: CapacityEmployee,
  from: IsoWeek,
  weeks: number,
): WeekCapacity[] {
  const result: WeekCapacity[] = [];

  for (let index = 0; index < weeks; index += 1) {
    result.push(weekCapacity(employee, addWeeks(from, index)));
  }

  return result;
}

// --- Abwesenheiten und Auslastung (AC-406) ----------------------------------------
//
// Entscheidungen E3 bis E6 in docs/stories/AC-406/ENTSCHEIDUNGEN.md. Wie oben
// in Hundertstelstunden als Ganzzahl, nie ueber `Number("16.5")` (Regel 10).

/** Die Stunden einer Abwesenheit — mehr braucht die Rechnung nicht, die Art nicht. */
export interface AbwesenheitsStunden {
  readonly hours: string;
}

/** Die um Abwesenheiten reduzierte Kapazitaet einer Woche (AC-406, AK 2). */
export interface VerfuegbareKapazitaet {
  /** Wie `WeekCapacity.planbar`: an mindestens einem Arbeitstag beschaeftigt. */
  readonly planbar: boolean;
  /** Die Soll-Kapazitaet der Woche (`WeekCapacity.hours`). */
  readonly sollHours: string;
  /** Die Summe der Abwesenheiten der Woche, gleich welcher Art. */
  readonly abwesendHours: string;
  /** Soll minus Abwesenheiten, nie unter null. */
  readonly verfuegbarHours: string;
  /**
   * HINWEIS, kein Fehler (E4): die Abwesenheiten uebersteigen das Soll und
   * wurden auf null begrenzt — etwa Urlaub 40 h plus Krankheit 8 h. Das Soll
   * kann sich nach der Erfassung geaendert haben; die Oberflaeche zeigt es an.
   */
  readonly abwesenheitUeberSoll: boolean;
}

/**
 * Die verfuegbare Kapazitaet einer Woche (AC-406, E4) — die EINE Stelle, an
 * der Abwesenheiten vom Soll abgezogen werden. Wochenraster (AC-402) und
 * Auslastung (AC-407) rufen sie auf und rechnen nicht daneben.
 *
 * `abwesenheiten` sind die Abwesenheiten DIESER Person in DIESER Woche. Eine
 * nicht planbare Woche (extern, vor Eintritt, nach Austritt) hat Soll 0 und
 * damit 0 verfuegbar; ihre Abwesenheiten — Altbestand nach einem spaeteren
 * Austritt — loesen keinen Hinweis aus.
 */
export function verfuegbareKapazitaet(
  woche: WeekCapacity,
  abwesenheiten: readonly AbwesenheitsStunden[],
): VerfuegbareKapazitaet {
  const soll = weeklyHoursToHundredths(woche.hours);
  const abwesend = abwesenheiten.reduce(
    (summe, abwesenheit) => summe + weeklyHoursToHundredths(abwesenheit.hours),
    0,
  );
  const verfuegbar = Math.max(0, soll - abwesend);

  return {
    planbar: woche.planbar,
    sollHours: woche.hours,
    abwesendHours: hundredthsToHours(abwesend),
    verfuegbarHours: hundredthsToHours(verfuegbar),
    abwesenheitUeberSoll: woche.planbar && abwesend > soll,
  };
}

/**
 * Die Auslastung einer Woche (AC-406, AK 4, E5) — genau eines von dreien:
 *
 *   prozent           geplante Stunden / verfuegbare Kapazitaet, ganzzahlig
 *                     kaufmaennisch gerundet (75, 100, 120 …)
 *   abwesend          die Person ist planbar, aber die Abwesenheiten haben die
 *                     ganze Woche aufgebraucht — KEIN Prozentwert
 *   keine_kapazitaet  die Woche hat schon ohne Abwesenheit kein Soll: nicht
 *                     planbar (extern, vor Eintritt, nach Austritt) oder Soll 0
 *
 * `konflikt`: trotzdem sind Stunden geplant. Nie Division durch null, nie
 * unendlich.
 */
export type Auslastung =
  | { readonly zustand: 'prozent'; readonly prozent: number }
  | { readonly zustand: 'abwesend'; readonly konflikt: boolean }
  | { readonly zustand: 'keine_kapazitaet'; readonly konflikt: boolean };

/**
 * Bezieht die geplanten Stunden auf die um Abwesenheiten REDUZIERTE
 * Kapazitaet (AK 4) — nicht wie die Bestands-Excel, die Abwesenheiten zur
 * Last addiert (E6). Welche Belegungen als geplant zaehlen (verbindlich,
 * freigegeben), entscheidet der Aufrufer (AC-407, `belastungInWoche`).
 */
export function auslastung(kapazitaet: VerfuegbareKapazitaet, geplantHours: string): Auslastung {
  const geplant = weeklyHoursToHundredths(geplantHours);
  const verfuegbar = weeklyHoursToHundredths(kapazitaet.verfuegbarHours);

  if (verfuegbar > 0) {
    // Kaufmaennisch: floor(100g / v + 1/2) = floor((200g + v) / 2v), exakt in Ganzzahlen.
    return {
      zustand: 'prozent',
      prozent: Math.floor((200 * geplant + verfuegbar) / (2 * verfuegbar)),
    };
  }

  const ohneSoll = !kapazitaet.planbar || weeklyHoursToHundredths(kapazitaet.sollHours) === 0;

  return { zustand: ohneSoll ? 'keine_kapazitaet' : 'abwesend', konflikt: geplant > 0 };
}

/**
 * Ueberlast einer Woche (AC-402, AK 3): mehr geplante Stunden als verfuegbar —
 * gegen die INDIVIDUELLE, um Abwesenheiten reduzierte Kapazitaet der Woche,
 * nie gegen eine feste Wochenstundenzahl. Gleichstand ist keine Ueberlast.
 * Eine Woche ohne verfuegbare Stunden ist ueberlastet, sobald etwas geplant
 * ist (dieselbe Lage wie `konflikt` in `auslastung`).
 */
export function istUeberlast(kapazitaet: VerfuegbareKapazitaet, geplantHours: string): boolean {
  return (
    weeklyHoursToHundredths(geplantHours) > weeklyHoursToHundredths(kapazitaet.verfuegbarHours)
  );
}

/** Eine Abwesenheit mit Art, wie der Pflegepfad sie liefert (`Absence`). */
export interface AbwesenheitMitArt {
  readonly employeeId: string;
  readonly woche: IsoWeek;
  readonly art: string;
  readonly hours: string;
}

/** „Abwesend" mit Stunden je Person und Woche — ohne Art (E3). */
export interface AbwesendInWoche {
  readonly employeeId: string;
  readonly woche: IsoWeek;
  readonly hours: string;
}

/**
 * Was jede Sicht ausser dem Pflegepfad von Abwesenheiten bekommt (E3):
 * je Person und Woche die SUMME der Stunden, ohne Art. Die Art traegt
 * Gesundheitsdaten (Krankheit); Wochenraster (AC-402), Auslastung (AC-407)
 * und Dashboards rufen diese Funktion SERVERSEITIG, bevor etwas den Server
 * verlaesst. Summiert wird, weil schon zwei getrennte Zeilen derselben Woche
 * verrieten, dass es zwei Arten sind.
 *
 * Neue Objekte, nie ein Spread: eine kuenftige Zusatzspalte am Pflegepfad
 * soll hier nicht still mitlaufen. Reihenfolge: nach Person, dann Woche.
 */
export function ohneArt(abwesenheiten: readonly AbwesenheitMitArt[]): AbwesendInWoche[] {
  const summen = new Map<string, { employeeId: string; woche: IsoWeek; summe: number }>();

  for (const abwesenheit of abwesenheiten) {
    const { isoYear, isoWeek } = abwesenheit.woche;
    const schluessel = `${abwesenheit.employeeId}|${mondayOf(abwesenheit.woche)}`;
    const bisher = summen.get(schluessel);

    if (bisher === undefined) {
      summen.set(schluessel, {
        employeeId: abwesenheit.employeeId,
        woche: { isoYear, isoWeek },
        summe: weeklyHoursToHundredths(abwesenheit.hours),
      });
    } else {
      bisher.summe += weeklyHoursToHundredths(abwesenheit.hours);
    }
  }

  return [...summen.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([, { employeeId, woche, summe }]) => ({
      employeeId,
      woche,
      hours: hundredthsToHours(summe),
    }));
}
